import { mapState, mapStores } from 'pinia';
import { productPurchaseAvailabilities, orderPaymentStatuses } from '@/Models';
import { getEffectivePurchaseLimit } from '@/Utils/ProductUtility';
import { useCartSummaryStore } from '@/Stores/Web/cartSummaryStore';
import { useProjectContextStore } from '@/Stores/Web/projectContextStore';
import { useProjectUserContextStore } from '@/Stores/Web/projectUserContextStore';
import { useUserContextStore } from '@/Stores/Web/userContextStore';

export default {
    props: {
        tooltipText: { default: null, type: String },
        purchaseLimit: { default: null, type: Number },
    },
    data() {
        return {
            model: {
                quantity: this.quantity,
            },
            productData: null,
        };
    },
    computed: {
        effectivePurchaseLimit() {
            const projectVersion = this.projectContextStore.projectContext.project.version;
            return getEffectivePurchaseLimit(this.effectivePurchaseLimitModel, this.getAvailableQuantityFromPledge, projectVersion, this.isAuthenticated);
        },
        effectivePurchaseLimitModel() {
            const hasStockLimits = this.stockLimits && Object.keys(this.stockLimits).length > 0;
            if (hasStockLimits) {
                const { remainingStockLimit, remainingPerUserLimit } = this.stockLimits;
                return {
                    productID: this.productId,
                    remainingStockLimit,
                    remainingPerUserLimit,
                };
            }

            if (this.productData) {
                const { productID, remainingStockLimit, remainingPerUserLimit } = this.productData;
                return { productID, remainingStockLimit, remainingPerUserLimit };
            }

            return {
                productID: this.productId,
                remainingStockLimit: this.purchaseLimit,
                remainingPerUserLimit: null,
            };
        },
        isDisabled() {
            return this.$validator.errors.any() || this.readOnly;
        },
        hasUnselectedOptions() {
            if (this.productData?.productSetItems?.length) {
                return this.productData.productSetItems.some(item => item.setItem.options?.some(o => !o.productOptionValueID));
            }
            return this.productData?.options?.some(o => !o.productOptionValueID) ?? false;
        },
        tooltipContent() {
            if (this.model.quantity < 1) {
                return this.Resources.Product.ProductValidationMinQuantityTooltipText;
            }

            if (this.hasUnselectedOptions) {
                return this.Resources.Product.ProductValidationUnselectedOptionsTooltipText;
            }

            if (this.tooltipText) {
                return this.tooltipText;
            }

            if (this.projectUserContext.hasOrderWithPaymentAction) {
                if (this.orderWithPaymentAction?.cart?.orderPaymentStatus === orderPaymentStatuses.processingPayment) {
                    return this.Resources.Product.ProductValidationPaymentProcessingTooltipText;
                }
                return this.projectContextStore.isProjectRetailType
                    ? this.Resources.Product.ProductValidationOrderNotFinishedTooltipText
                    : this.Resources.Product.ProductValidationPledgeNotFinishedTooltipText;
            }

            if (this.projectUserContext.hasLockedActiveOrder) {
                return this.Resources.ProjectHome.LockedOrderTooltip;
            }

            const purchaseLimit = this.effectivePurchaseLimit;

            if (purchaseLimit.status === productPurchaseAvailabilities.stockLimitReached)
                return this.Resources.Product.ProductValidationStockLimitReachedTooltipText;

            if (purchaseLimit.status === productPurchaseAvailabilities.perUserLimitReached)
                return this.Resources.Product.ProductValidationLimitReachedTooltipText;

            if (purchaseLimit.status === productPurchaseAvailabilities.availableOnlyForLoggedInUsers)
                return this.Resources.Product.ItemLimitedToLoggedInUsersTooltip;

            if (purchaseLimit.value !== null) {
                const pluralizedCount = this.Resources.pluralize(purchaseLimit.value, this.Resources.Product.ProductLimitPluralizedCount);
                return this.Resources.format(this.Resources.Product.ProductValidationLimitInfoTooltipText, pluralizedCount);
            }

            return this.Resources.Product.ProductValidationLimitReachedTooltipText;
        },
        ...mapState(useProjectUserContextStore, ['projectUserContext', 'orderWithPaymentAction']),
        ...mapState(useCartSummaryStore, ['getAvailableQuantityFromPledge']),
        ...mapState(useUserContextStore, ['isAuthenticated']),
        ...mapStores(useProjectContextStore),
    },
};